// The ONE delete dialog every admin screen uses — so deleting always looks,
// reads and behaves the same everywhere (superadmin-only, always audited).
//
// Props:
//   title            "Delete this order?"
//   summary          one-line identity of what's about to go
//   consequences     array of strings — plain-English bullets of what happens
//   requirePassword  true for the big irreversible deletes (orders, stock cars)
//   busy / error     submit state from the caller
//   onConfirm({ password, reason })   onClose()
import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiEye, FiEyeOff, FiX } from 'react-icons/fi';
import Alert from './Alert.jsx';
import Button from './Button.jsx';

const DeleteConfirm = ({
  title = 'Delete this record?',
  summary = '',
  consequences = [],
  requirePassword = false,
  busy = false,
  error = '',
  onConfirm,
  onClose,
}) => {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSubmit = !busy && (!requirePassword || password.trim().length > 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50">
              <FiAlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-900">{title}</h3>
              {summary && <p className="mt-0.5 truncate text-sm text-slate-500">{summary}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {consequences.length > 0 && (
          <ul className="mt-4 space-y-1.5 rounded-xl bg-rose-50/60 px-4 py-3">
            {consequences.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-xs leading-5 text-rose-800">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-rose-400" />
                {c}
              </li>
            ))}
          </ul>
        )}

        {/* Every delete is recorded — say so up front, it is a feature */}
        <p className="mt-3 text-[11px] leading-4 text-slate-400">
          This will be saved in <b className="text-slate-500">Deleted Records</b> with your name,
          the time, and a full copy of the data — it can always be read back.
        </p>

        {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

        <label className="mt-4 block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-700">
            Reason <span className="font-normal text-slate-400">(optional, shown in the record)</span>
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            placeholder="e.g. duplicate entry, wrong customer"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
          />
        </label>

        {requirePassword && (
          <label className="mt-3 block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">Your admin password</span>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                autoFocus
                placeholder="Required for this delete"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-10 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </button>
            </div>
          </label>
        )}

        <div className="mt-5 flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={busy} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm({ password: password.trim(), reason: reason.trim() })}
            loading={busy}
            disabled={!canSubmit}
            className="flex-1"
          >
            Yes, delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirm;
